import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortByPopularity } from 'webapp/utils/common';

import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SourceMetricsGridItemComponent from 'webapp/components/source/metrics/grid-item';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TemplatesPublicPopularBoardsComponent from 'webapp/components/templates/public/popular-boards';

export default class SourcePopupAfterConnectComponent extends PopupInnerModalComponent<PopupData.Source.AfterConnect> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('connect') declare connectService: Service.Connect;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked isLoading;

	origin = 'connection_added_choice_popup';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isLoading = true;
		void this.mmMetricsService
			.requestMetrics()
			.then(() => {
				next(this.scheduleReposition);
			})
			.finally(() => {
				this.isLoading = false;
			});
	}

	get sourceType() {
		return this.popupData.sourceType;
	}

	get sourceId() {
		return this.popupData.sourceId;
	}

	get isQbOnly() {
		return this.popupData.isQbOnly;
	}

	get showPublicTemplates() {
		return this.popupData.showPublicTemplates;
	}

	@cached
	get metrics() {
		return sortByPopularity(
			this.mmMetricsService.getMetricsForSpaceAccessId(this.sourceId),
		).slice(0, 6);
	}

	get image() {
		const type = this.sourceType.isSheets ? 'sheets' : 'custom';
		return `/images/svg/${type}_connected.svg`;
	}

	get headerImage() {
		if (this.isQbOnly) {
			return '/images/svg/metrics.svg';
		}

		return '/images/svg/wand.svg';
	}

	get headerTitle() {
		if (this.isQbOnly) {
			return 'Start with setting up the first metric';
		}

		return 'Let’s help you get started with some suggested items';
	}

	get showDatasetOptions() {
		return (
			this.sourceType.isDatasetSupported &&
			!this.isQbOnly &&
			this.currentUserService.isOnboarded
		);
	}

	triggerNewConnectionAddedChoicePopupEvent(choice: string) {
		const trackingChoiceName = choice === 'templates' ? 'public_templates' : choice;

		// choices: public_templates, metrics, request_databoard, guide, custom_metrics
		this.analyticsService.event(TrackingEvent.NEW_CONNECTION_ADDED_CHOICE_POPUP, {
			choice: trackingChoiceName,
		});
	}

	@action
	openQb() {
		this.triggerNewConnectionAddedChoicePopupEvent('custom_metrics');
		this.mmCustomQueriesService.openCreateNewPopup('listing', undefined, this.sourceId);
	}

	@action
	openGuide() {
		this.triggerNewConnectionAddedChoicePopupEvent('guide');
		window.open(this.sourceType.qbLinkWithFallback, '_blank');
	}

	@action
	createNewDataset() {
		const billingFeature = this.sourceType.isSheets
			? BillingFeature.ADVANCED_DATA_SOURCES_LIMITED || BillingFeature.ADVANCED_DATA_SOURCES
			: BillingFeature.ADVANCED_DATA_SOURCES;

		const showLimitationsPopup =
			!this.currentUserService.isAdmin || this.accountService.isClient;
		if (this.billingService.checkIfCan(billingFeature, showLimitationsPopup)) {
			this.connectService.addNewDataset(this.sourceType.key, {
				sourceId: this.sourceId,
				dataSourceName: this.sourceType.name,
			});
			this.analyticsService.event(TrackingEvent.NEW_DATASET_INITIATED, {
				origin: this.origin,
				datasource: this.sourceType.name,
			});
		} else if (!showLimitationsPopup) {
			this.billingService.openPlanUpgradeForFeaturePopup(
				billingFeature,
				'datasets',
				(hasUpgraded: boolean) => {
					if (hasUpgraded) {
						this.connectService.addNewDataset(this.sourceType.key, {
							sourceId: this.sourceId,
							dataSourceName: this.sourceType.name,
						});
					}
				},
			);
		}
	}
	<template>
		<div class="title title--vertical {{if this.isQbOnly 'title--qb-only'}}">
			<img src={{this.headerImage}} height="48" width="48" alt="Header" />
			<span class="text">{{this.headerTitle}}</span>
		</div>
		<div class="content {{if this.isQbOnly 'content--qb-only'}}">

			{{#if this.isQbOnly}}

				<div class="section section--custom">

					<p>
						Great job for connecting the data source.
						<br />
						Now, let's move forward to create a custom metric to visualize data.
					</p>

					<img src={{this.image}} height="100" width="auto" alt />

					<SubmitButtonComponent
						@text="New metric"
						@size="big"
						@noDelay={{true}}
						@onTrigger={{this.openQb}}
					/>

					<a class="guide theme-gray" {{on "click" this.openGuide}}>
						<CommonUiIconComponent @name="Book" @size="md" />
						View help guide
					</a>
				</div>

			{{else}}
				<div class="section section--metrics">
					<div class="section__header">
						<p>Most popular metrics</p>
						<LinkTo @route="metric-library">Browse all metrics &gt;</LinkTo>
					</div>

					{{#if this.isLoading}}
						<div class="loader-ct">
							<BarLoaderComponent />
						</div>
					{{else}}
						<ul class="flex-ct flex-ct--size-small-block-metric">
							{{#each this.metrics as |metric|}}
								<SourceMetricsGridItemComponent
									@model={{metric}}
									@trackingOriginForAdd={{this.origin}}
								/>
							{{/each}}

							{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
							<li class="flex-item flex-item--fake"></li>
							<li class="flex-item flex-item--fake"></li>
						</ul>
					{{/if}}

					{{#if this.showDatasetOptions}}
						<div class="section section--datasets">
							<div class="card">
								<div class="left">
									<img
										src="/images/datasets/explore_datasets.svg"
										alt="Explore datasets"
									/>
								</div>
								<div class="right">
									<h3>
										Build custom metrics with datasets
									</h3>
									<p>
										Create custom metrics by selecting specific columns from
										views. This will give you the ability to view, filter, merge
										and export raw data.
									</p>
									<div class="buttons">
										<a
											role="button"
											class="button icon"
											{{on "click" this.createNewDataset}}
										>
											<CommonUiIconComponent @name="Plus" @size="sm" />
											New dataset
										</a>
										<a
											role="button"
											href="https://help.databox.com/understanding-datasets"
											target="_blank"
											class="button theme-blue-outlined"
										>
											<CommonUiIconComponent @name="Book" @size="sm" />Learn
											more
										</a>
									</div>
								</div>
							</div>
						</div>
					{{/if}}

					{{#if this.showPublicTemplates}}
						<TemplatesPublicPopularBoardsComponent
							@sourceType={{this.sourceType}}
							@trackingOrigin={{this.origin}}
							@onLoaded={{this.scheduleReposition}}
						/>
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
